import { sortBy } from 'lodash'
import _get from 'lodash/get'
import moment from 'moment'

export default {
	removeNonNumericCharsOMD(phone) {
		return phone.replace(/\D/g, '')
	},

	// Method to format the phone number as xxx-xxx-xxxx
	newFormatPhoneNumberOMD(phone) {
		const cleaned = ('' + phone).replace(/\D/g, '') // Remove non-numeric characters
		const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)

		if (match) {
			return `${match[2]}-${match[3]}-${match[4]}`
		}

		return phone // If it doesn't match the format, return the original
	},

	validateCallBackNumberDashed(number) {
		return !(
			!number ||
			number.length < 12 ||
			number[0] === '0' ||
			number[0] === '1'
		)
	},

	formatPhoneNumberAsStandard(number) {
		// Remove non-numeric characters
		let formatted = number.replace(/\D/g, '')

		// Add the phone number format (xxx-xxx-xxxx)
		if (formatted.length <= 3) {
			number = formatted // First part
		} else if (formatted.length <= 6) {
			number = `${formatted.slice(0, 3)}-${formatted.slice(3)}` // Second part
		} else {
			number = `${formatted.slice(0, 3)}-${formatted.slice(
				3,
				6
			)}-${formatted.slice(6, 10)}` // Final format
		}
		return number
	},

	getDraftValue(key, sid) {
		if (!key || !sid) return ''
		const drafts = JSON.parse(localStorage.getItem('saved_drafts') || '{}')
		return drafts[`${key}_${sid}`] || ''
	},

	setDraftValue(key, value, sid) {
		if (!key || !sid) return
		const drafts = JSON.parse(localStorage.getItem('saved_drafts') || '{}')
		drafts[`${key}_${sid}`] = value
		localStorage.setItem('saved_drafts', JSON.stringify(drafts))
	},

	resetDraftValues() {
		localStorage.removeItem('saved_drafts')
	},

	containNewAlphabets(msg, tmplt, minCount, maxCount) {
		const message = (msg || '').trim().replace(/\s+/g, ' ')
		const template = (tmplt || '').trim() + '\n'

		const countTotalWhitespaces = str => {
			const matches = str.match(/(\r\n)|\r|\n| /g)
			return matches ? matches.length : 0
		}

		const getWords = text =>
			text
				.replace(/[\r\n\t]+/g, ' ')
				.trim()
				.split(/\s+/)

		const messageWords = getWords((message || '').toLowerCase())
		const templateWords = getWords((template || '').toLowerCase())
		let remainingMsgWords = []
		let templateWordsMissingCount = 0
		const templateWordsCopy = [...templateWords]

		for (let i of messageWords) {
			const index = templateWordsCopy.indexOf(i)
			if (index === -1) {
				remainingMsgWords.push(i)
			} else {
				templateWordsCopy.splice(index, 1)
			}
		}

		for (let i of templateWords) {
			const index = messageWords.indexOf(i)
			if (index === -1) {
				templateWordsMissingCount++
			}
		}

		const remainingMsgChars = remainingMsgWords.join('')
		const totalWhitespacesMsg = countTotalWhitespaces(message)
		const totalWhitespacesTemplate = countTotalWhitespaces(template)

		const resultLength =
			remainingMsgChars.length +
			totalWhitespacesMsg -
			(totalWhitespacesTemplate -
				templateWordsMissingCount -
				(templateWordsMissingCount < templateWords.length ? 1 : 0))

		return resultLength >= minCount && resultLength <= maxCount
	},

	normalizeString(str) {
		return str
			.toLowerCase()
			.replace(/\r\n/g, '\n') // normalize CRLF to LF
			.replace(/\s+/g, ' ') // replace multiple whitespace (spaces, tabs, newlines) with single space
			.trim()
	},

	getSpecificCallAttributes(call, attribute, defaultValue = null) {
		const callAttributes = _get(call, 'call_attributes', [])
		const found = callAttributes?.find(
			attr => attr.internal_name === attribute
		)
		const value =
			typeof found?.value === 'string' ? found.value.trim() : found?.value
		return value ? value : defaultValue
	},

	isWMACall(call) {
		const events = call?.events || []
		const hasTransferEvent = events.some(
			event =>
				event.type === 'smart_transfer' ||
				event.type === 'external_transfer'
		)
		const isWmaEnabled = !!call?.wma_allow_reply
		const isCallCompleted = !!call?.completed_at
		return isWmaEnabled && !(isCallCompleted && hasTransferEvent)
	},

	capitalizeFirstLetter(string) {
		if (!string || typeof string !== 'string') return ''
		return string.charAt(0).toUpperCase() + string.slice(1)
	},

	sortPartners(partners = []) {
		return sortBy(partners, p =>
			p.name
				?.toLowerCase()
				.replace(/[()]/g, '') // remove brackets
				.replace(/[\s-]/g, '') // remove spaces & hyphens
				.trim()
		)
	},

	async copyToClipboard(data) {
		try {
			await navigator.clipboard.writeText(data)
			return true
		} catch (err) {
			return false
		}
	},

	generateSummary(call) {
		try {
			if (!call || Object.keys(call).length === 0) {
				return ''
			}

			const lines = []
			const labelWidth = 20
			const maxLineLength = 60 // Max characters for value per line

			// Helper function to format label-value pairs with alignment
			const formatLine = (label, value) => {
				return `${label.padEnd(labelWidth)}${value}`
			}

			// Helper function to wrap text by whole words
			const wrapTextByWords = (text, maxLen) => {
				const words = text.split(/\s+/)
				const lines = []
				let currentLine = ''

				words.forEach(word => {
					if (!word) return
					if (currentLine.length === 0) {
						currentLine = word
					} else if (currentLine.length + 1 + word.length <= maxLen) {
						currentLine += ' ' + word
					} else {
						lines.push(currentLine)
						currentLine = word
					}
				})

				if (currentLine) {
					lines.push(currentLine)
				}

				return lines
			}

			// Helper function to format long text with wrapping
			const formatLongText = (label, value) => {
				if (!value) {
					return `${label.padEnd(labelWidth)}${value}`
				}

				const indent = ' '.repeat(labelWidth)

				// Check if value has newlines or is long
				if (!value.includes('\n') && value.length <= maxLineLength) {
					return `${label.padEnd(labelWidth)}${value}`
				}

				const result = []
				const paragraphs = value.split('\n')

				paragraphs.forEach((paragraph, index) => {
					const wrappedLines = wrapTextByWords(
						paragraph,
						maxLineLength
					)

					if (index === 0) {
						// First paragraph starts with label
						wrappedLines.forEach((line, lineIndex) => {
							if (lineIndex === 0) {
								result.push(
									`${label.padEnd(labelWidth)}${line}`
								)
							} else {
								result.push(`${indent}${line}`)
							}
						})
						if (wrappedLines.length === 0) {
							result.push(`${label.padEnd(labelWidth)}`)
						}
					} else {
						// Subsequent paragraphs start with indent
						wrappedLines.forEach(line => {
							result.push(`${indent}${line}`)
						})
						if (wrappedLines.length === 0) {
							result.push(`${indent}`)
						}
					}
				})

				return result.join('\n')
			}

			// Header
			lines.push('Clarus Care')
			lines.push('')
			lines.push('Message Details')
			lines.push('')
			lines.push(
				formatLine('Exported:', moment().format('MM/DD/YYYY hh:mm A'))
			)
			lines.push('')

			// Date/Time of Call
			if (call.created_at) {
				const timestamp = moment
					.utc(call.created_at)
					.local()
					.format('MM/DD/YYYY hh:mm A')
				lines.push(formatLine('Received:', timestamp))
			}

			// Patient Name
			lines.push(
				formatLine('Patient Name:', call.patient_name || 'Unavailable')
			)

			// Provider
			const providerName =
				call.patient_provider?.full_name || 'Unavailable'
			lines.push(formatLine('Provider:', providerName))

			// Phone Number
			let phoneNumber = 'Unavailable'
			if (call.callback_number) {
				try {
					phoneNumber = this.newFormatPhoneNumberOMD(
						call.callback_number
					)
				} catch (e) {
					phoneNumber = call.callback_number
				}
			}
			lines.push(formatLine('Phone Number:', phoneNumber))

			// Patient DOB
			lines.push(
				formatLine('Patient DOB:', call.patient_dob || 'Unavailable')
			)

			// Patient Message
			lines.push(
				formatLongText(
					'Patient Message:',
					call.patient_message || 'Unavailable'
				)
			)

			lines.push('')

			// Notes
			lines.push('Notes:')
			lines.push('')
			// Add header row (aligned with data: timestamp is 19 chars "MM/DD/YYYY hh:mm A")
			lines.push(`${'Date'.padEnd(20)}  ${'Created By'.padEnd(17)}  Note`)
			lines.push(
				'---------------------------------------------------------------------------------'
			)
			const notes = [...call.notes].sort((a, b) => b.id - a.id)
			notes.forEach(note => {
				const timestamp = moment
					.utc(note.created_at)
					.local()
					.format('MM/DD/YYYY hh:mm A')
				const userName = note.user
					? `${note.user.first_name || ''} ${note.user.last_name ||
							''}`.trim() || 'Unknown'
					: 'Unknown'
				const noteContent =
					note?.type === 'completion'
						? `Completion note: ${note.content}`
						: note.content || ''
				const notePrefix = `${timestamp} - ${userName} - `
				const noteIndent = ' '.repeat(notePrefix.length)
				const noteMaxLength = 80 - notePrefix.length

				// Split by newlines first, then wrap each paragraph by words
				const paragraphs = noteContent.split('\n')
				paragraphs.forEach((paragraph, pIndex) => {
					const wrappedLines = wrapTextByWords(
						paragraph,
						noteMaxLength
					)

					if (pIndex === 0) {
						// First paragraph starts with prefix
						wrappedLines.forEach((line, lineIndex) => {
							if (lineIndex === 0) {
								lines.push(`${notePrefix}${line}`)
							} else {
								lines.push(`${noteIndent}${line}`)
							}
						})
						if (wrappedLines.length === 0) {
							lines.push(`${notePrefix}`)
						}
					} else {
						// Subsequent paragraphs start with indent
						wrappedLines.forEach(line => {
							lines.push(`${noteIndent}${line}`)
						})
						if (wrappedLines.length === 0) {
							lines.push(`${noteIndent}`)
						}
					}
				})
			})
			lines.push('')

			// History
			if (call.events && call.events.length > 0) {
				lines.push('Activity History:')
				lines.push('')
				// Add header row (aligned with data: timestamp is 19 chars "MM/DD/YYYY hh:mm A")
				lines.push(`${'Date'.padEnd(19)}   Activity`)
				lines.push(
					'---------------------------------------------------------------------------------'
				)
				const events = [...call.events].sort((a, b) => b.id - a.id)
				events.forEach(event => {
					const timestamp = moment
						.utc(event.created_at)
						.local()
						.format('MM/DD/YYYY hh:mm A')
					const eventPrefix = `${timestamp} - `
					const eventSummary = event.summary || ''
					const eventIndent = ' '.repeat(eventPrefix.length)
					const eventMaxLength = 80 - eventPrefix.length

					// Split by newlines first, then wrap each paragraph by words
					const paragraphs = eventSummary.split('\n')
					paragraphs.forEach((paragraph, pIndex) => {
						const wrappedLines = wrapTextByWords(
							paragraph,
							eventMaxLength
						)

						if (pIndex === 0) {
							// First paragraph starts with prefix
							wrappedLines.forEach((line, lineIndex) => {
								if (lineIndex === 0) {
									lines.push(`${eventPrefix}${line}`)
								} else {
									lines.push(`${eventIndent}${line}`)
								}
							})
							if (wrappedLines.length === 0) {
								lines.push(`${eventPrefix}`)
							}
						} else {
							// Subsequent paragraphs start with indent
							wrappedLines.forEach(line => {
								lines.push(`${eventIndent}${line}`)
							})
							if (wrappedLines.length === 0) {
								lines.push(`${eventIndent}`)
							}
						}
					})
				})
			}

			const result = lines.join('\n')
			return result
		} catch (error) {
			console.error('Error generating summary:', error)
			return ''
		}
	},
}
